/**
 * Centralised SEO indexability rules.
 *
 * The idea is simple: weak/empty/duplicate/parameter pages emit
 * `noindex, follow`. Strong content pages stay indexable with a canonical
 * URL on the production domain. All thresholds live here so they can be
 * tuned without touching individual page components.
 *
 * Production domain is hard-coded because canonicals must always point
 * to the public origin regardless of preview/staging.
 */

export const PRODUCTION_DOMAIN = "https://doing-good.org";

// Minimum-offer thresholds. Below these counts, a hub page is considered
// thin content unless it is on the explicit priority list.
export const MIN_OFFERS_CITY = 10;
export const MIN_OFFERS_CAUSE = 10;
export const MIN_OFFERS_COUNTRY = 10;
export const MIN_OFFERS_CATEGORY = 10;
// Combo (City×Cause / City×Category) pages now ship with intro, featured,
// related-causes and backlink sections — that "useful context" lets us
// safely include combos from 3+ active offers (down from the legacy 5).
export const MIN_OFFERS_COMBO = 3;

/**
 * Priority city slugs — always indexable even if listing count temporarily
 * dips below the threshold. These are the strongest hubs we actively
 * curate and promote.
 */
export const PRIORITY_CITY_SLUGS = new Set([
  "berlin",
  "munich",
  "hamburg",
  "frankfurt",
  "vienna",
  "zurich",
]);

/**
 * Priority cause slugs. The original product brief named several causes
 * that do not (yet) exist as distinct categories in the database
 * ("Women Support", "Homelessness", "Children & Youth", "Food & Poverty").
 * Until those become first-class causes with real listings we pin the
 * priority list to the strongest existing equivalents: Refugees & Migration,
 * Climate & Environment, Poverty & Hunger (≈ Food & Poverty),
 * Education (≈ Children & Youth), Health & Wellbeing, Mental Health.
 */
export const PRIORITY_CAUSE_SLUGS = new Set([
  "refugees-and-migration",
  "climate-and-environment",
  "poverty-and-hunger",
  "education",
  "health-and-wellbeing",
  "mental-health",
]);

/**
 * Minimum description length (after trim) for an offer detail page to be
 * considered "meaningful" for SEO purposes.
 */
export const MIN_OFFER_DESCRIPTION_CHARS = 80;

/**
 * Decide whether a single offer detail page has enough data to be worth
 * indexing. Weak / incomplete listings are kept crawlable but excluded
 * from the index so they cannot dilute the site's overall quality score.
 */
export function isOfferDetailIndexable(offer) {
  if (!offer) return false;
  if (!offer.title || !offer.organization) return false;
  const desc = String(offer.description || "").trim();
  if (desc.length < MIN_OFFER_DESCRIPTION_CHARS) return false;
  const hasContext =
    Boolean(offer.city) ||
    Boolean(offer.country) ||
    Boolean(offer.cause_area) ||
    Boolean(offer.category) ||
    Boolean(offer.url);
  return hasContext;
}

const INDEX_FOLLOW = "index, follow";
const NOINDEX_FOLLOW = "noindex, follow";
const NOINDEX_NOFOLLOW = "noindex, nofollow";

/**
 * Decide the value of <meta name="robots"> for a given page.
 *
 * @param {Object} opts
 * @param {string} opts.pageType - one of: home, about, imprint, privacy,
 *   browseHub, offersList, offerDetail, city, cause, country, category,
 *   cityCombo, search, notFound, auth, account, listOffer
 * @param {number} [opts.totalOffers] - listing count for hub pages
 * @param {boolean} [opts.isPriority] - explicit priority override
 * @param {boolean} [opts.hasParams] - search/filter params present
 * @returns {string} the robots directive
 */
export function decideRobots(opts = {}) {
  const { pageType, totalOffers, isPriority, hasParams } = opts;

  switch (pageType) {
    case "home":
    case "about":
    case "imprint":
    case "privacy":
    case "browseHub":
    case "offerDetail":
    case "listOffer":
      return INDEX_FOLLOW;

    case "city":
      if (isPriority) return INDEX_FOLLOW;
      return (totalOffers ?? 0) >= MIN_OFFERS_CITY ? INDEX_FOLLOW : NOINDEX_FOLLOW;

    case "cause":
      if (isPriority) return INDEX_FOLLOW;
      return (totalOffers ?? 0) >= MIN_OFFERS_CAUSE ? INDEX_FOLLOW : NOINDEX_FOLLOW;

    case "country":
      return (totalOffers ?? 0) >= MIN_OFFERS_COUNTRY ? INDEX_FOLLOW : NOINDEX_FOLLOW;

    case "category":
      return (totalOffers ?? 0) >= MIN_OFFERS_CATEGORY ? INDEX_FOLLOW : NOINDEX_FOLLOW;

    case "cityCombo":
      return (totalOffers ?? 0) >= MIN_OFFERS_COMBO ? INDEX_FOLLOW : NOINDEX_FOLLOW;

    case "offersList":
      // Bare /offers is the directory entry point — keep indexable.
      // Any query param turns it into a filter/search variant — noindex.
      return hasParams ? NOINDEX_FOLLOW : INDEX_FOLLOW;

    case "search":
      return NOINDEX_FOLLOW;

    case "notFound":
    case "auth":
    case "account":
      return NOINDEX_NOFOLLOW;

    default:
      return INDEX_FOLLOW;
  }
}

/**
 * Build a canonical URL on the production domain. Always strips query
 * params and hash so canonicals never drift across filter combinations.
 */
export function buildCanonical(path = "/") {
  const clean = String(path).split("?")[0].split("#")[0] || "/";
  const prefixed = clean.startsWith("/") ? clean : `/${clean}`;
  return `${PRODUCTION_DOMAIN}${prefixed}`;
}

export const isCityPriority = (slug) => PRIORITY_CITY_SLUGS.has(String(slug || "").toLowerCase());
export const isCausePriority = (slug) => PRIORITY_CAUSE_SLUGS.has(String(slug || "").toLowerCase());

/**
 * Convert a human cause/category name to its URL slug.
 * Matches the backend convention used in seo_endpoints.py / data ingest:
 *   "Climate & Environment"  -> "climate-and-environment"
 *   "Mental Health"          -> "mental-health"
 *   "Refugees & Migration"   -> "refugees-and-migration"
 * Used for client-side discovery links into /c/:city/:cause where the city
 * payload only carries the cause name, not its slug.
 */
export function slugifyTaxonomy(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/* ------------------------------------------------------------------ */
/*  Metadata builders                                                  */
/*  Centralised so titles & descriptions stay consistent and unique.   */
/* ------------------------------------------------------------------ */

const SITE_NAME = "doing-good.org";

/** Clamp text to a sensible length, prefer cutting at a word boundary. */
function clamp(text, max) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:!\-–—\s]+$/, "") + "…";
}

/**
 * Build a `<title>` with consistent " | doing-good.org" suffix.
 *
 * Google's SERP truncates titles around 580–600 px (≈ 60–70 chars
 * depending on letter width). We allow labels up to 65 chars so most
 * templated titles ("Explore volunteering and donation opportunities")
 * survive intact; only very long offer titles get an ellipsis.
 */
export function buildPageTitle(label) {
  if (!label) return SITE_NAME;
  const suffix = ` | ${SITE_NAME}`;
  return `${clamp(label, 65)}${suffix}`;
}

/** Clamp a meta description to ~155 chars (Google snippet limit). */
export function buildMetaDescription(text) {
  return clamp(text, 158);
}

/**
 * Centralised metadata generator for all indexable page types.
 * Returns `{ title, description }`. Pages should still pass canonical
 * and robots separately via the existing helpers.
 *
 * `data` shape depends on pageType:
 *   home:        none
 *   about:       none
 *   imprint:     none
 *   privacy:     none
 *   offersList:  { hasParams }
 *   city:        { cityName, countryName, totalOffers }
 *   cause:       { causeName, totalOffers }
 *   cityCombo:   { cityName, causeName, totalOffers }
 *   country:     { countryName, totalOffers }
 *   category:    { categoryName, totalOffers }
 *   offerDetail: { title, organization, description, city, country, causeArea }
 *   browseHub:   { kind: 'city'|'cause'|'country'|'category' }
 */
export function buildPageMeta(pageType, data = {}) {
  switch (pageType) {
    case "home":
      return {
        title: buildPageTitle("Find ways to help across Europe"),
        description: buildMetaDescription(
          "Discover volunteering, donation and professional support opportunities from NGOs and local organizations across Europe."
        ),
      };

    case "about":
      return {
        title: buildPageTitle("About — ways to help across Europe"),
        description: buildMetaDescription(
          "Learn how doing-good.org helps people discover volunteering, donation and professional support opportunities across Europe."
        ),
      };

    case "imprint":
      return {
        title: buildPageTitle("Imprint"),
        description: buildMetaDescription(
          "Legal imprint for doing-good.org as required by §5 TMG."
        ),
      };

    case "privacy":
      return {
        title: buildPageTitle("Privacy Policy"),
        description: buildMetaDescription(
          "GDPR-compliant privacy policy for doing-good.org — what data we collect, why and how long."
        ),
      };

    case "offersList": {
      const filtered = Boolean(data.hasParams);
      return {
        title: buildPageTitle(
          filtered
            ? "Filtered opportunities"
            : "Explore volunteering and donation opportunities"
        ),
        description: buildMetaDescription(
          "Browse active opportunities to volunteer, donate or support NGOs and local organizations across Europe."
        ),
      };
    }

    case "city": {
      const city = data.cityName || "your city";
      const country = data.countryName ? `, ${data.countryName}` : "";
      return {
        title: buildPageTitle(`Volunteer and donate in ${city}`),
        description: buildMetaDescription(
          `Browse volunteering, donation and local support opportunities from NGOs and community organizations in ${city}${country}.`
        ),
      };
    }

    case "cause": {
      const cause = data.causeName || "social impact";
      return {
        title: buildPageTitle(`${cause} opportunities in Europe`),
        description: buildMetaDescription(
          `Discover ways to support ${cause} organizations across Europe through volunteering, donations and professional help.`
        ),
      };
    }

    case "cityCombo": {
      const city = data.cityName || "your city";
      const cause = data.causeName || "social impact";
      return {
        title: buildPageTitle(`${cause} volunteering in ${city}`),
        description: buildMetaDescription(
          `Explore ${cause} volunteering and support opportunities from local organizations in ${city}.`
        ),
      };
    }

    case "country": {
      const country = data.countryName || "Europe";
      return {
        title: buildPageTitle(`Volunteer and donate in ${country}`),
        description: buildMetaDescription(
          `Browse volunteering, donation and pro bono opportunities from NGOs and community organizations in ${country}.`
        ),
      };
    }

    case "category": {
      const category = data.categoryName || "Opportunities";
      return {
        title: buildPageTitle(`${category} opportunities across Europe`),
        description: buildMetaDescription(
          `Browse ${String(category).toLowerCase()} opportunities from NGOs and community organizations across Europe.`
        ),
      };
    }

    case "browseHub": {
      const kind = data.kind;
      const map = {
        city: ["Browse opportunities by city", "Explore volunteering, donation and pro bono opportunities by European city."],
        cause: ["Browse opportunities by cause", "Find European NGOs working on the causes you care about — volunteering, donations and pro bono."],
        country: ["Browse opportunities by country", "Discover volunteering, donation and pro bono opportunities country by country across Europe."],
        category: ["Browse opportunities by category", "Browse opportunities by type — volunteering, donations, pro bono services and impact investing."],
      };
      const [label, desc] = map[kind] || map.city;
      return { title: buildPageTitle(label), description: buildMetaDescription(desc) };
    }

    case "offerDetail": {
      const t = String(data.title || "").trim();
      const org = String(data.organization || "").trim();
      const desc = String(data.description || "").trim();
      const titlePart = org ? `${t} by ${org}` : t || "Opportunity";
      const fallbackDesc = org
        ? `Learn more about this opportunity from ${org} and find ways to help through ${SITE_NAME}.`
        : `Learn more about this opportunity and find ways to help through ${SITE_NAME}.`;
      return {
        title: buildPageTitle(titlePart),
        // Prefer the offer's own description (much better for snippets);
        // fall back to a calm generic line when description is missing.
        description: buildMetaDescription(desc || fallbackDesc),
      };
    }

    case "listOffer":
      return {
        title: buildPageTitle("For organizations — list an opportunity"),
        description: buildMetaDescription(
          "List a volunteering, donation or pro bono opportunity from your NGO or community initiative. Free for non-profits across Europe."
        ),
      };

    case "notFound":
      return {
        title: buildPageTitle("Page not found"),
        description: buildMetaDescription("The page you're looking for doesn't exist on doing-good.org."),
      };

    case "auth":
      return {
        title: buildPageTitle("Sign in or create account"),
        description: buildMetaDescription("Sign in to save opportunities and follow organizations on doing-good.org."),
      };

    case "account":
      return {
        title: buildPageTitle("Your account"),
        description: buildMetaDescription("Your saved opportunities and account details on doing-good.org."),
      };

    default:
      return {
        title: SITE_NAME,
        description: "Find ways to help across Europe.",
      };
  }
}
